import { useMemo } from 'react';
import { getImageUrl } from '@/shared/utils/image';
import type { StoreItem } from '../types/get-store-items';
import { getDisplayPrice } from '../utils/display-price';
import { getDiscountPercent, getPromotion } from '../utils/promotion';
import { PromotionPrice } from './promotion-price';
import { QuantityPromotionChip } from './quantity-promotion-chip';
import { isFlagOn } from '../utils/store-benefits';

interface StoreItemGridCardProps {
  item: StoreItem;
  onItemClick?: (item: StoreItem) => void;
  /**
   * Selo da promoção por quantidade (PRAT-184), ex.: "2º com 50%". Só os
   * produtos que participam recebem; null/ausente = sem selo.
   */
  quantityPromotionLabel?: string | null;
  /** PRAT-187: menor preço das faixas de atacado ("A partir de"). */
  wholesaleFrom?: number | null;
}

/**
 * Card quadrado de produto utilizado na grade responsiva da loja.
 * Imagem em destaque no topo (proporção 1:1) e informações abaixo.
 */
export function StoreItemGridCard({
  item,
  onItemClick,
  quantityPromotionLabel,
  wholesaleFrom = null,
}: StoreItemGridCardProps) {
  const handleClick = () => {
    onItemClick?.(item);
  };

  const display = useMemo(() => getDisplayPrice(item), [item]);
  const price = wholesaleFrom ?? display.price;
  const regularPrice = wholesaleFrom !== null ? null : display.regularPrice;
  const hasRequiredAddons = display.hasRequiredAddons || wholesaleFrom !== null;
  const promotion = useMemo(() => getPromotion(item), [item]);
  const isPopular = isFlagOn(item.is_popular);

  return (
    <div
      className="flex flex-col cursor-pointer rounded-xl border border-gray-200 overflow-hidden bg-white hover:border-gray-300 hover:shadow-sm transition-all clickable-card fade-in-scale"
      onClick={handleClick}
    >
      {/* Imagem quadrada do item */}
      <div className="relative aspect-square w-full overflow-hidden bg-gray-100">
        <img
          loading="lazy"
          decoding="async"
          src={getImageUrl(item?.image)}
          alt={item.name}
          className="w-full h-full object-cover object-center"
          onError={e => {
            e.currentTarget.src = '/fallback.svg';
          }}
        />
        {/* PRAT-110: o desconto foi para o selo verde ao lado do preço; a foto
            fica com o "Mais pedido", como nas vitrines de delivery. */}
        {isPopular && (
          <span className="absolute top-2 left-2 rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur-sm">
            Mais pedido
          </span>
        )}
        {/* PRAT-184: só quem participa da promoção por quantidade leva o selo */}
        {quantityPromotionLabel && (
          <QuantityPromotionChip
            label={quantityPromotionLabel}
            className="absolute top-2 right-2"
          />
        )}
      </div>

      {/* Informações do item */}
      <div className="flex flex-1 flex-col justify-between gap-2 p-3">
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium text-night line-clamp-2 leading-snug">{item.name}</p>
          {item.description && (
            <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">{item.description}</p>
          )}
        </div>
        <PromotionPrice
          price={price}
          regularPrice={regularPrice}
          percent={promotion ? getDiscountPercent(item) : null}
          prefix={hasRequiredAddons ? 'A partir de' : undefined}
        />
      </div>
    </div>
  );
}
